import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cycleResultLabel, type Cycle, type CycleFull } from '@visionqc/shared';
import { useIsAdmin } from '../api/auth';
import { procguardApi } from '../api/procguard';

const RESULT_TONE: Record<string, string> = { ok: 'var(--good)', nok: 'var(--bad)', aborted: 'var(--warn)', running: 'var(--accent)' };

/** Read-only view of recent cycles and their deviations — the runtime record. */
export function ProcGuardCyclesView() {
  const isAdmin = useIsAdmin();
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [open, setOpen] = useState<CycleFull | null>(null);

  useEffect(() => { procguardApi.listCycles({ limit: 100 }).then(setCycles).catch(() => undefined); }, []);
  if (!isAdmin) return <div className="panel muted">Cycles require the ADMIN role.</div>;

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Jakatori — cycles</h1>
        <Link className="btn sm" to="/procguard">← Workstations</Link>
      </div>
      {cycles.length === 0 ? (
        <div className="panel muted">No cycles recorded yet. They appear once a workstation is enabled and running.</div>
      ) : (
        <div className="panel">
          <table style={{ width: '100%' }}>
            <thead><tr><th>Started</th><th>Result</th><th>Operator</th><th>Duration</th><th></th></tr></thead>
            <tbody>
              {cycles.map((c) => (
                <tr key={c.id}>
                  <td className="muted" style={{ fontSize: 13 }}>{new Date(c.startedAt).toLocaleString()}</td>
                  <td><span className="badge" style={{ ['--tone' as string]: RESULT_TONE[c.status], color: RESULT_TONE[c.status] }}>{cycleResultLabel(c.status)}</span></td>
                  <td>{c.operator ?? <span className="muted">—</span>}{c.training ? ' (training)' : ''}</td>
                  <td className="muted">{c.durationMs != null ? `${(c.durationMs / 1000).toFixed(1)}s` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <a className="btn sm" href={procguardApi.reportUrl(c.id)} target="_blank" rel="noreferrer">Report</a>{' '}
                    <button className="btn sm" onClick={() => procguardApi.getCycle(c.id).then(setOpen)}>Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Cycle #{open.id} — {open.status}</strong>
            <button className="btn sm" onClick={() => setOpen(null)}>Close</button>
          </div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
            <div>
              <strong style={{ fontSize: 13 }}>Steps</strong>
              <table style={{ width: '100%', marginTop: 6 }}>
                <tbody>
                  {open.steps.map((s) => (
                    <tr key={s.id}><td>{s.seq}. {s.name}</td><td><span className="badge">{s.status}</span></td><td className="muted">{s.durationMs != null ? `${(s.durationMs / 1000).toFixed(1)}s` : ''}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <strong style={{ fontSize: 13 }}>Deviations</strong>
              {open.deviations.length === 0 ? <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>None.</div> : (
                <table style={{ width: '100%', marginTop: 6 }}>
                  <tbody>
                    {open.deviations.map((d) => (
                      <tr key={d.id}><td style={{ color: d.severity === 'critical' ? 'var(--bad)' : 'var(--warn)' }}>{d.kind}</td><td className="muted" style={{ fontSize: 12 }}>{d.detail}{d.overrideBy ? ` · overridden by ${d.overrideBy}` : ''}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
